import Service, { service } from "@ember/service";
import { getOwnConfig, macroCondition } from "@embroider/macros";
import { trackedObject } from "@ember/reactive/collections";
import { FeatureFlag } from "glesys-controlpanel/utils/feature-flags";
import type { FeatureFlagId } from "glesys-controlpanel/utils/feature-flags";
import type OrganizationModel from "glesys-controlpanel/models/organization";
import type { DevToolsService } from "glesys-controlpanel/services/dev-tools";

class FeatureFlags extends Service {
  isEnabled(organization: OrganizationModel | null, flag: FeatureFlagId): boolean {
    return organization?.featureFlags?.includes(flag) ?? false;
  }
}

let FeatureFlagsService: typeof FeatureFlags = FeatureFlags;

if (macroCondition(getOwnConfig<{ devtools: boolean }>().devtools)) {
  type Overrides = Partial<Record<FeatureFlagId, boolean | null>>;

  class DevFeatureFlags extends FeatureFlags {
    @service declare devTools: DevToolsService;

    overrides: Overrides = trackedObject({});

    constructor(...args: ConstructorParameters<typeof FeatureFlags>) {
      super(...args);

      const saved = this.devTools.load<Overrides>("feature-flags") ?? {};
      const known = Object.values(FeatureFlag) as FeatureFlagId[];
      Object.entries(saved)
        .filter(([flag]) => known.includes(flag as FeatureFlagId))
        .forEach(([flag, value]) => this.setOverride(flag as FeatureFlagId, value));
    }

    isEnabled(organization: OrganizationModel | null, flag: FeatureFlagId): boolean {
      return this.overrides[flag] ?? super.isEnabled(organization, flag);
    }

    getOverride(flag: FeatureFlagId): boolean | null {
      return this.overrides[flag] ?? null;
    }

    setOverride(flag: FeatureFlagId, value: boolean | null): void {
      this.overrides[flag] = typeof value === "boolean" ? value : null;
    }
  }

  FeatureFlagsService = DevFeatureFlags;
}

export default FeatureFlagsService;
